import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { ExpensePriority } from "../../lib/types";
import { Expense } from "../../lib/types";
import { formatCurrency, ExpenseAmountContext } from "../../lib/expenseUtils";
import {
  summarizeRecurringBills,
  occurrencesPerMonth,
  BillFrequency,
  PriorityFilterValue,
  RecurringBillLine,
} from "../../lib/recurringBillsUtils";
import RecurringPriorityBoard from "./RecurringPriorityBoard";
import RecurringBillRow, { PRIORITY_LABEL } from "./RecurringBillRow";
import { estimateLlcRevenueForNetMonthly } from "../../lib/llcRevenueUtils";

// Replaces the "/ charge" caption. Hover or click reveals the LLC revenue you'd
// have to produce (through a Deschutes County, Oregon single-member LLC) to
// take home this group's monthly amount after federal + Oregon + self-
// employment tax. The number tracks the Include-debts / Include-postponed /
// Cumulative toggles because `netMonthly` is recomputed from the shown lines.
const LlcRevenueTooltip: React.FC<{ netMonthly: number; label: string }> = ({
  netMonthly,
  label,
}) => {
  const [open, setOpen] = React.useState(false);
  const est = React.useMemo(
    () => estimateLlcRevenueForNetMonthly(netMonthly),
    [netMonthly]
  );
  const title = (
    <Box sx={{ p: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
        Revenue to take home {label} {formatCurrency(netMonthly)}/mo
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
        Through a Deschutes County, Oregon LLC you'd need to produce about{" "}
        <b>{formatCurrency(est.grossMonthly)}/mo</b> (
        {formatCurrency(est.grossAnnual)}/yr) in revenue.
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
      >
        Est. taxes {formatCurrency(est.taxMonthly)}/mo (~
        {Math.round(est.effectiveRate * 100)}% effective) — federal + Oregon
        income + self-employment. Single filer, no dependents, 2025 rates,
        pass-through profit. Estimate only.
      </Typography>
    </Box>
  );
  return (
    <Tooltip
      title={title}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      disableInteractive={false}
      componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
    >
      <IconButton
        size="small"
        onClick={() => setOpen((o) => !o)}
        sx={{ p: 0.25, ml: 0.25, color: "text.secondary" }}
        aria-label="Revenue needed to take home this amount"
      >
        <InfoOutlinedIcon sx={{ fontSize: "0.95rem" }} />
      </IconButton>
    </Tooltip>
  );
};

interface RecurringBillsDialogProps {
  open: boolean;
  onClose: () => void;
  expenses: Expense[];
  debts?: any[];
  context?: ExpenseAmountContext;
  onAddBill?: () => void;
  onEditBill?: (id: string) => void;
  onDeleteBill?: (id: string) => void;
  onEditDebt?: (debtId: string) => void;
  onDeleteDebt?: (debtId: string) => void;
  onPostponeDebt?: (debtId: string, until: string | null | "clear") => void;
  onPostponeExpense?: (
    expenseId: string,
    until: string | null | "clear"
  ) => void;
  onSetExpensePriority?: (
    expenseId: string,
    priority: ExpensePriority | null
  ) => void;
  // Set (or clear) an expense's numeric priority rank for the priority board.
  onSetExpenseRank?: (expenseId: string, rank: number | null) => void;
  // When an edit panel is open alongside, shift this dialog to the left and make
  // it non-modal so both panels are visible and interactive side by side.
  shiftLeft?: boolean;
  // "dialog" (default) renders inside a modal; "page" renders inline as a full
  // tab/page with no modal chrome.
  variant?: "dialog" | "page";
}

// How each cadence's charge folds into a monthly figure, shown as a caption.
const CADENCE_NOTE: Record<BillFrequency, string> = {
  weekly: "× 4 / mo",
  biweekly: "× 2 / mo",
  monthly: "× 1 / mo",
  quarterly: "÷ 3 / mo",
  semiannual: "÷ 6 / mo",
  annually: "÷ 12 / mo",
};

const PRIORITY_OPTIONS: PriorityFilterValue[] = [
  "emergency",
  "essential",
  "optional",
  "unassigned",
];

/**
 * "Recurring Bills Only" — the normal recurring bills, ignoring due dates and
 * overdue state, grouped by cadence. Supports a priority filter, optional debt
 * inclusion, optional postponed bills, and a cumulative monthly/annual total.
 */
const RecurringBillsDialog: React.FC<RecurringBillsDialogProps> = ({
  open,
  onClose,
  expenses,
  debts,
  context,
  onAddBill,
  onEditBill,
  onDeleteBill,
  onEditDebt,
  onDeleteDebt,
  onPostponeDebt,
  onPostponeExpense,
  onSetExpensePriority,
  onSetExpenseRank,
  shiftLeft,
  variant = "dialog",
}) => {
  const [postponeAnchor, setPostponeAnchor] =
    React.useState<HTMLElement | null>(null);
  const [postponeTarget, setPostponeTarget] = React.useState<{
    type: "debt" | "expense";
    id: string;
  } | null>(null);
  const [postponeDate, setPostponeDate] = React.useState("");
  // Items temporarily hidden from the totals for this open session only.
  const [excluded, setExcluded] = React.useState<Set<string>>(new Set());
  // Reset the temporary hide set whenever the dialog is (re)opened.
  React.useEffect(() => {
    if (open) setExcluded(new Set());
  }, [open]);
  const toggleExcluded = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [sortBy, setSortBy] = React.useState<
    "amount-desc" | "amount-asc" | "name-asc" | "priority"
  >("priority");
  const [annualized, setAnnualized] = React.useState(false);
  const [includeDebts, setIncludeDebts] = React.useState(false);
  const [includePostponed, setIncludePostponed] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [priorityFilter, setPriorityFilter] = React.useState<
    PriorityFilterValue[]
  >([]);

  const summary = useMemo(
    () =>
      summarizeRecurringBills(expenses, {
        debts,
        context,
        includeDebts,
        includePostponed,
        priorityFilter,
        search,
      }),
    [
      expenses,
      debts,
      context,
      includeDebts,
      includePostponed,
      priorityFilter,
      search,
    ]
  );

  const applyPostpone = (until: string | null | "clear") => {
    if (postponeTarget) {
      if (postponeTarget.type === "debt" && onPostponeDebt)
        onPostponeDebt(postponeTarget.id, until);
      else if (postponeTarget.type === "expense" && onPostponeExpense)
        onPostponeExpense(postponeTarget.id, until);
    }
    setPostponeAnchor(null);
  };

  // Open the postpone popover for a row, resolving the correct target id
  // (debt row, debt-linked bill, or plain expense).
  const requestPostpone = (line: RecurringBillLine, anchorEl: HTMLElement) => {
    setPostponeAnchor(anchorEl);
    setPostponeTarget(
      line.kind === "debt"
        ? { type: "debt", id: line.id.replace(/^debt-/, "") }
        : line.linkedDebtId
          ? { type: "debt", id: line.linkedDebtId }
          : { type: "expense", id: line.id }
    );
    setPostponeDate("");
  };

  const { groups, totalCount } = summary;
  // Recompute counts and totals excluding items the user temporarily hid.
  const isIncluded = (id: string) => !excluded.has(id);
  const includedLines = groups
    .flatMap((g) => g.lines)
    .filter((l) => isIncluded(l.id));
  const monthlyTotal = includedLines.reduce(
    (s, l) => s + l.amount * occurrencesPerMonth(l.frequency),
    0
  );
  const annualTotal = monthlyTotal * 12;
  // Extra monthly revenue an Oregon (Deschutes County) LLC must produce to
  // cover taxes on a given monthly take-home — shown as a "+ taxes" line.
  const taxesFor = (monthly: number) =>
    estimateLlcRevenueForNetMonthly(monthly).taxMonthly;
  const billCount = includedLines.length;

  // "Cumulative" folds every item into a per-month figure (weekly ×4,
  // quarterly ÷3, annually ÷12) and lists them all under a single Monthly
  // group. Off, items keep their native charge grouped by cadence.
  const cumulative = annualized;
  const lineDisplayAmount = (l: (typeof includedLines)[number]) =>
    cumulative ? l.amount * occurrencesPerMonth(l.frequency) : l.amount;
  const sortLines = (lines: typeof includedLines) =>
    [...lines].sort((a, b) => {
      if (sortBy === "name-asc") return a.name.localeCompare(b.name);
      const av = lineDisplayAmount(a);
      const bv = lineDisplayAmount(b);
      return sortBy === "amount-asc" ? av - bv : bv - av;
    });
  const displayGroups = cumulative
    ? [
        {
          key: "cumulative-monthly",
          label: "Monthly",
          note: "per-month equivalent",
          lines: groups.flatMap((g) => g.lines),
        },
      ]
    : groups.map((g) => ({
        key: g.frequency,
        label: g.label,
        note: CADENCE_NOTE[g.frequency],
        lines: g.lines,
      }));

  const inner = (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span>Recurring Expenses Only</span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {onAddBill && (
              <Tooltip title="Add expense">
                <IconButton size="small" color="primary" onClick={onAddBill}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
            {variant !== "page" && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Your normal recurring expenses, ignoring due dates and overdue. Broken
          down by how often they charge.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          label="Search"
          placeholder="Type to search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Autocomplete
          multiple
          size="small"
          options={PRIORITY_OPTIONS}
          value={priorityFilter}
          onChange={(_e, value) => setPriorityFilter(value)}
          getOptionLabel={(o) => PRIORITY_LABEL[o]}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                priorityFilter.length === 0 ? "Filter by priority…" : ""
              }
              label="Priorities shown"
            />
          )}
          sx={{ mb: 1.5 }}
        />
        <TextField
          select
          size="small"
          label="Sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          SelectProps={{ native: true }}
          sx={{ mb: 1.5, minWidth: 180 }}
        >
          <option value="amount-desc">Largest amount</option>
          <option value="amount-asc">Smallest amount</option>
          <option value="name-asc">Name (A–Z)</option>
          <option value="priority">Priority (drag to rank)</option>
        </TextField>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Chip
            size="small"
            label={
              priorityFilter.length > 0
                ? `${billCount} of ${totalCount} shown`
                : `${billCount} recurring ${billCount === 1 ? "item" : "items"}`
            }
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeDebts}
                  onChange={(e) => setIncludeDebts(e.target.checked)}
                />
              }
              label="Include debts"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includePostponed}
                  onChange={(e) => setIncludePostponed(e.target.checked)}
                />
              }
              label="Include postponed"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={annualized}
                  onChange={(e) => setAnnualized(e.target.checked)}
                />
              }
              label="Cumulative"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
              }}
            />
          </Box>
        </Box>

        {billCount === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No recurring bills match the current filters.
          </Typography>
        )}

        {sortBy === "priority" && onSetExpenseRank && (
          <RecurringPriorityBoard
            lines={includedLines}
            onSetRank={onSetExpenseRank}
            excluded={excluded}
            onToggleExcluded={toggleExcluded}
            showPostpone={Boolean(onPostponeDebt || onPostponeExpense)}
            onRequestPostpone={requestPostpone}
            onSetExpensePriority={onSetExpensePriority}
            onEditBill={onEditBill}
            onDeleteBill={onDeleteBill}
            onEditDebt={onEditDebt}
            onDeleteDebt={onDeleteDebt}
          />
        )}

        {sortBy !== "priority" &&
          displayGroups.map((group) => {
            const inc = group.lines.filter((l) => isIncluded(l.id));
            const subtotal = inc.reduce((s, l) => s + lineDisplayAmount(l), 0);
            // Monthly-equivalent of this group, independent of the Cumulative
            // toggle, used for the LLC revenue gross-up tooltip.
            const groupMonthly = inc.reduce(
              (s, l) => s + l.amount * occurrencesPerMonth(l.frequency),
              0
            );
            return (
              <Box key={group.key} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {group.label}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      {group.note}
                    </Typography>
                  </Typography>
                  <Box sx={{ textAlign: "right" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {formatCurrency(subtotal)}
                        {cumulative && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            / mo
                          </Typography>
                        )}
                      </Typography>
                      <LlcRevenueTooltip
                        netMonthly={groupMonthly}
                        label={group.label}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.2 }}
                    >
                      + {formatCurrency(taxesFor(groupMonthly))} taxes
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 0.5 }} />
                {sortLines(group.lines).map((line) => (
                  <RecurringBillRow
                    key={line.id}
                    line={line}
                    displayAmount={lineDisplayAmount(line)}
                    hidden={excluded.has(line.id)}
                    onToggleExcluded={toggleExcluded}
                    showPostpone={Boolean(onPostponeDebt || onPostponeExpense)}
                    onRequestPostpone={requestPostpone}
                    onSetExpensePriority={onSetExpensePriority}
                    onEditBill={onEditBill}
                    onDeleteBill={onDeleteBill}
                    onEditDebt={onEditDebt}
                    onDeleteDebt={onDeleteDebt}
                  />
                ))}
              </Box>
            );
          })}

        {billCount > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {formatCurrency(monthlyTotal)}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    / mo
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(annualTotal)} / yr
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "warning.main" }}
                >
                  + {formatCurrency(taxesFor(monthlyTotal))} taxes / mo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(
                    estimateLlcRevenueForNetMonthly(monthlyTotal).grossMonthly
                  )}{" "}
                  / mo revenue needed
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <Popover
        open={Boolean(postponeAnchor)}
        anchorEl={postponeAnchor}
        onClose={() => setPostponeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 1.5, width: 240 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Postpone
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            label="Until"
            InputLabelProps={{ shrink: true }}
            value={postponeDate}
            onChange={(e) => setPostponeDate(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Stack spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={!postponeDate}
              onClick={() => {
                applyPostpone(postponeDate);
              }}
            >
              Postpone until date
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => applyPostpone(null)}
            >
              Postpone indefinitely
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => applyPostpone("clear")}
            >
              Un-postpone
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );

  if (variant === "page") {
    return <Box sx={{ maxWidth: 720, mx: "auto" }}>{inner}</Box>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideBackdrop={shiftLeft}
      disableEnforceFocus={shiftLeft}
      disableAutoFocus={shiftLeft}
      sx={
        shiftLeft
          ? {
              pointerEvents: "none",
              "& .MuiDialog-container": { justifyContent: "flex-start" },
            }
          : undefined
      }
      PaperProps={
        shiftLeft ? { sx: { pointerEvents: "auto", ml: 2 } } : undefined
      }
    >
      {inner}
    </Dialog>
  );
};

export default RecurringBillsDialog;
